import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import type {PixKeyActions} from '@gatewee/components/v2';
import {GenericCrudService} from '@gatewee/services/generic-crud.service';
import {RecipientPixKey} from '@gatewee/types/recipient-pix-keys';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';

@Component({
  selector: 'app-recipient-pix-keys',
  templateUrl: './recipient-pix-keys.component.html',
  styleUrls: ['./recipient-pix-keys.component.css'],
  standalone: false,
})
export class RecipientPixKeysComponent implements OnInit {
  private crudService: GenericCrudService<RecipientPixKey>;

  pixKeys: RecipientPixKey[] = [];
  page = 1;
  pageSize = 10;
  total = 0;
  from = 0;
  take = 10;

  breadcrumb = [
    {label: 'nav.breadcrumb.settings', path: '/panel'},
    {label: 'nav.menu.pixKeys'},
  ];

  pixKeyActions: PixKeyActions = {
    onRetrieveKeys: () =>
      this.crudService.create({} as RecipientPixKey).pipe(
        tap(() => {
          this.fetchKeys();
          this.alerts
            .open(this.translate.instant('features.pixKeys.retrieveSuccess'), {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            })
            .subscribe();
        }),
        map(() => undefined),
        catchError((err: HttpErrorResponse) => {
          this.alerts
            .open(
              err?.error?.message ??
                this.translate.instant('features.pixKeys.retrieveError'),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
          return throwError(() => err);
        }),
      ),
    loadKeys: () => this.fetchKeys(),
    onUpdateDescription: (id, description) =>
      this.crudService.update(id, {description}).pipe(
        tap(() => {
          this.fetchKeys();
          this.alerts
            .open(this.translate.instant('features.pixKeys.updateSuccess'), {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            })
            .subscribe();
        }),
        map(() => undefined),
        catchError((err: HttpErrorResponse) => {
          this.alerts
            .open(
              err?.error?.message ??
                this.translate.instant('features.pixKeys.updateError'),
              {
                label: this.translate.instant('common.status.error'),
                appearance: 'error',
              },
            )
            .subscribe();
          return throwError(() => err);
        }),
      ),
  };

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private alerts: TuiAlertService,
  ) {
    this.crudService = new GenericCrudService<RecipientPixKey>(
      '/recipients-pix-keys/recipient',
      this.http,
    );
  }

  ngOnInit(): void {
    this.fetchKeys();
  }

  fetchKeys(): void {
    this.crudService.findAll().subscribe({
      next: (data: RecipientPixKey[]) => {
        this.pixKeys = data;
        this.total = data.length;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error fetching PIX keys:', error);
      },
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.from = (page - 1) * this.take;
    this.fetchKeys();
  }
}
